import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { 
  calculateMonthlyPayment, 
  calculateTotalInterest, 
  calculateTotalRepaid,
  generateAmortizationSchedule,
  applyInterestFreeze,
  calculateAPR,
  calculateEffectiveAPR
} from '../../utils/calculations';
import * as Sentry from '@sentry/browser';

const InterestAccrualCalculator = ({ onCalculate }) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      principal: 1000,
      monthlyInterestRate: 0.5,
      termInMonths: 60,
      interestType: 'compound',
      includeInterestFreeze: false,
      freezeStartMonth: 3,
      freezeEndMonth: 6
    }
  });

  const includeInterestFreeze = watch('includeInterestFreeze');
  const monthlyInterestRate = watch('monthlyInterestRate');
  
  // Calculate annual rate from monthly rate
  const calculateAnnualFromMonthly = (monthlyRate) => {
    if (!monthlyRate || monthlyRate === 0) return 0;
    // Using compound formula: (1 + monthly_rate/100)^12 - 1
    const decimalRate = monthlyRate / 100;
    const annualRate = (Math.pow(1 + decimalRate, 12) - 1) * 100;
    return annualRate;
  };

  const calculatedAnnualRate = calculateAnnualFromMonthly(monthlyInterestRate);

  const onSubmit = async (data) => {
    try {
      setIsCalculating(true);
      console.log('Calculating interest accrual with:', data);
      
      // Parse form values
      const principal = parseFloat(data.principal);
      const monthlyInterestRate = parseFloat(data.monthlyInterestRate);
      const termInMonths = parseInt(data.termInMonths, 10);
      const interestType = data.interestType;
      const includeInterestFreeze = data.includeInterestFreeze;
      
      // Convert monthly rate to annual rate for calculations
      const annualInterestRate = calculateAnnualFromMonthly(monthlyInterestRate);
      
      console.log('Monthly rate:', monthlyInterestRate, '% - Annual rate:', annualInterestRate, '%');
      
      // Generate standard amortization schedule
      const standardSchedule = generateAmortizationSchedule(principal, annualInterestRate, termInMonths);
      
      // Calculate standard APR (without interest freeze)
      const apr = calculateAPR(principal, annualInterestRate, termInMonths);
      
      let schedule = standardSchedule;
      let totalInterest = calculateTotalInterest(principal, annualInterestRate, termInMonths);
      let totalRepaid = calculateTotalRepaid(principal, annualInterestRate, termInMonths);
      let interestSavings = null;
      let effectiveAPR = null;
      
      // Apply interest freeze if selected
      if (includeInterestFreeze) {
        const freezeStartMonth = parseInt(data.freezeStartMonth, 10);
        const freezeEndMonth = parseInt(data.freezeEndMonth, 10);
        
        // Validate freeze months
        if (freezeStartMonth < 1 || freezeStartMonth > termInMonths) {
          throw new Error(`Freeze start month must be between 1 and ${termInMonths}`);
        }
        if (freezeEndMonth < freezeStartMonth || freezeEndMonth > termInMonths) {
          throw new Error(`Freeze end month must be between ${freezeStartMonth} and ${termInMonths}`);
        }
        
        console.log('Applying interest freeze from month', freezeStartMonth, 'to', freezeEndMonth);
        
        // Apply interest freeze to get modified schedule
        schedule = applyInterestFreeze(standardSchedule, freezeStartMonth, freezeEndMonth);
        
        // Calculate actual totals from the modified schedule
        const actualTotalInterest = schedule.reduce((sum, payment) => {
          return sum.plus(payment.interest || 0);
        }, calculateMonthlyPayment(0, 0, 1).minus(calculateMonthlyPayment(0, 0, 1))); // Creates Decimal zero
        
        // Calculate actual total repaid (only count non-zero payments)
        const actualTotalRepaid = schedule.reduce((sum, payment) => {
          return sum.plus(payment.payment || 0);
        }, calculateMonthlyPayment(0, 0, 1).minus(calculateMonthlyPayment(0, 0, 1))); // Creates Decimal zero
        
        // Calculate interest savings (standard total interest minus actual interest paid)
        const standardTotalInterest = calculateTotalInterest(principal, annualInterestRate, termInMonths);
        interestSavings = standardTotalInterest.minus(actualTotalInterest);
        
        // Update totals for the results
        totalInterest = actualTotalInterest;
        totalRepaid = actualTotalRepaid;
        
        // Calculate effective APR with interest freeze
        effectiveAPR = calculateEffectiveAPR(principal, actualTotalRepaid, termInMonths);
        
        console.log('Standard total interest:', standardTotalInterest.toFixed(2));
        console.log('Actual total interest with freeze:', actualTotalInterest.toFixed(2));
        console.log('Interest savings:', interestSavings.toFixed(2));
      }
      
      // Calculate key metrics for display
      const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termInMonths);
      
      // Prepare results
      const results = {
        calculatorType: 'interest',
        principal,
        monthlyInterestRate,
        annualInterestRate,
        apr,
        effectiveAPR,
        termInMonths,
        interestType,
        monthlyPayment,
        totalInterest,
        totalRepaid,
        schedule,
        standardTotalInterest: calculateTotalInterest(principal, annualInterestRate, termInMonths),
        standardTotalRepaid: calculateTotalRepaid(principal, annualInterestRate, termInMonths)
      };
      
      // Add interest freeze details if applicable
      if (includeInterestFreeze) {
        results.includeInterestFreeze = true;
        results.freezeStartMonth = parseInt(data.freezeStartMonth, 10);
        results.freezeEndMonth = parseInt(data.freezeEndMonth, 10);
        results.interestSavings = interestSavings;
      }
      
      console.log('Interest accrual calculation results:', results);
      
      // Pass results to parent component
      onCalculate(results);
    } catch (error) {
      console.error('Error calculating interest accrual:', error);
      
      // Capture the error in Sentry with additional context
      Sentry.captureException(error, {
        tags: {
          component: 'InterestAccrualCalculator',
          calculatorType: 'interest'
        },
        extra: {
          formData: data,
          principal: data.principal,
          monthlyInterestRate: data.monthlyInterestRate,
          termInMonths: data.termInMonths,
          includeInterestFreeze: data.includeInterestFreeze,
          freezeStartMonth: data.freezeStartMonth,
          freezeEndMonth: data.freezeEndMonth
        }
      });
      
      alert(`Error: ${error.message || 'An error occurred during calculation. Please check your input values.'}`);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="card">
      <h2 className="section-title">Interest Accrual Calculator</h2>
      <p className="mb-4 text-gray-600">
        Calculate how interest accrues on your loan and simulate interest freezes during forbearance periods.
      </p>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Principal Amount (£)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="0.01"
              {...register("principal", { 
                required: "Principal amount is required", 
                min: { value: 1, message: "Amount must be positive" },
                valueAsNumber: true
              })}
            />
            {errors.principal && (
              <p className="mt-1 text-sm text-red-600">{errors.principal.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Interest Rate (%)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="0"
              step="0.01"
              {...register("monthlyInterestRate", { 
                required: "Monthly interest rate is required",
                min: { value: 0, message: "Rate must be 0 or positive" },
                valueAsNumber: true
              })}
            />
            {errors.monthlyInterestRate && (
              <p className="mt-1 text-sm text-red-600">{errors.monthlyInterestRate.message}</p>
            )}
            {monthlyInterestRate && (
              <p className="mt-1 text-sm text-gray-500">
                Annual rate: {calculatedAnnualRate.toFixed(2)}%
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Term (months)
            </label>
            <input
              type="number"
              className="form-input box-border"
              min="1"
              step="1"
              {...register("termInMonths", { 
                required: "Loan term is required",
                min: { value: 1, message: "Term must be at least 1 month" },
                valueAsNumber: true
              })}
            />
            {errors.termInMonths && (
              <p className="mt-1 text-sm text-red-600">{errors.termInMonths.message}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interest Calculation Method
            </label>
            <select
              className="form-input box-border"
              {...register("interestType")}
            >
              <option value="compound">Compound (Interest on Interest)</option>
              <option value="simple">Simple (No Interest on Interest)</option>
            </select>
          </div>
          
          <div className="col-span-2">
            <div className="flex items-center">
              <input
                id="includeInterestFreeze"
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                {...register("includeInterestFreeze")}
              />
              <label htmlFor="includeInterestFreeze" className="ml-2 text-sm font-medium text-gray-700">
                Include interest freeze period (e.g., for forbearance)
              </label>
            </div>
          </div>
          
          {includeInterestFreeze && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Freeze Start Month
                </label>
                <input
                  type="number"
                  className="form-input box-border"
                  min="1"
                  step="1"
                  {...register("freezeStartMonth", { 
                    required: "Start month is required",
                    min: { value: 1, message: "Month must be at least 1" },
                    valueAsNumber: true
                  })}
                />
                {errors.freezeStartMonth && (
                  <p className="mt-1 text-sm text-red-600">{errors.freezeStartMonth.message}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Freeze End Month
                </label>
                <input
                  type="number"
                  className="form-input box-border"
                  min="1"
                  step="1"
                  {...register("freezeEndMonth", { 
                    required: "End month is required",
                    min: { value: 1, message: "Month must be at least 1" },
                    valueAsNumber: true
                  })}
                />
                {errors.freezeEndMonth && (
                  <p className="mt-1 text-sm text-red-600">{errors.freezeEndMonth.message}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Enter the monthly interest rate, and the annual equivalent will be calculated automatically using compound interest formula: (1 + monthly rate)^12 - 1. Interest freezes are often part of forbearance agreements, where the lender agrees not to charge interest for a specific period. The calculator will show how much interest you save during the freeze period.
              </p>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <button 
            type="submit" 
            className="btn btn-primary cursor-pointer"
            disabled={isCalculating}
          >
            {isCalculating ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InterestAccrualCalculator;